import { useState, useMemo, useEffect } from "react";
import DataTable from "react-data-table-component";
import type { Template } from "../../types/template";
import { dataTableCustomStyles } from "../../config/dataTableTheme";
import { DEFAULT_LIST_PAGE_SIZE } from "../../constants/listPagination";
import { AddinDataTablePagination } from "../molecules/AddinDataTablePagination";
import { Funnel, Search } from "lucide-react";
import {
  CONTRACT_DATE_RANGE_OPTIONS,
  type ContractDateRangeValue
} from "../../constants/contractFilters";

type TemplateLibraryViewProps = {
  templates: Template[];
  isLoading?: boolean;
  isOpeningDraft?: boolean;
  error?: string | null;
  onTemplateSelect: (id: string) => void;
  /** When template list failed to load, retry without full page reload. */
  onRetryLoad?: () => void;
};

const templateCreatedWithinDays = (template: Template, days: number): boolean => {
  if (!template.createdAt) {
    return false;
  }
  const created = new Date(template.createdAt).getTime();
  if (Number.isNaN(created)) {
    return false;
  }
  const cutoff = Date.now() - days * 24 * 60 * 60 * 1000;
  return created >= cutoff;
};

export const TemplateLibraryView = ({
  templates,
  isLoading = false,
  isOpeningDraft = false,
  error = null,
  onTemplateSelect,
  onRetryLoad
}: TemplateLibraryViewProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [selectedDateRangeDays, setSelectedDateRangeDays] = useState<
    ContractDateRangeValue | undefined
  >(undefined);
  const [isDateFilterOpen, setIsDateFilterOpen] = useState(false);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearchTerm(searchTerm), 200);
    return () => window.clearTimeout(t);
  }, [searchTerm]);

  const activeDateFilterLabel = useMemo(
    () =>
      CONTRACT_DATE_RANGE_OPTIONS.find((opt) => opt.value === selectedDateRangeDays)?.label ?? null,
    [selectedDateRangeDays]
  );

  const filteredTemplates = useMemo(() => {
    let result = templates;
    if (debouncedSearchTerm.trim()) {
      const term = debouncedSearchTerm.toLowerCase();
      result = result.filter(
        (t) =>
          t.name.toLowerCase().includes(term) || t.templateType.toLowerCase().includes(term)
      );
    }
    if (selectedDateRangeDays !== undefined) {
      result = result.filter((t) => templateCreatedWithinDays(t, selectedDateRangeDays));
    }
    return result;
  }, [templates, debouncedSearchTerm, selectedDateRangeDays]);

  const columns = useMemo(
    () => [
      {
        name: "Template Name",
        selector: (row: Template) => row.name,
        cell: (row: Template) => (
          <p className="font-medium text-foreground" data-tag="allowRowEvents">
            {row.name}
          </p>
        ),
        grow: 2
      },
      {
        name: "Template Type",
        selector: (row: Template) => row.templateType,
        cell: (row: Template) => (
          <p className="text-sm text-muted-foreground" data-tag="allowRowEvents">
            {row.templateType}
          </p>
        )
      }
    ],
    []
  );

  if (error) {
    return (
      <div className="space-y-4">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Template Library</h2>
          <p className="text-sm text-muted-foreground">Choose a template to review</p>
        </div>
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
        {onRetryLoad && (
          <button
            type="button"
            onClick={onRetryLoad}
            className="rounded-lg border border-[var(--border)] bg-input-background px-4 py-2 text-sm font-medium text-foreground transition-all duration-200 ease-in-out hover:bg-accent"
          >
            Retry
          </button>
        )}
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Template Library</h2>
          <p className="text-sm text-muted-foreground">Choose a template to review</p>
        </div>
        <p className="text-muted-foreground">Loading templates…</p>
      </div>
    );
  }

  return (
    <div className="relative space-y-4">
      {isOpeningDraft ? (
        <div
          className="fixed inset-0 z-[100] flex flex-col items-center justify-center gap-3 bg-background/85 px-4 backdrop-blur-sm"
          role="alert"
          aria-busy="true"
          aria-live="polite"
        >
          <div className="h-10 w-10 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          <p className="text-center text-sm font-medium text-foreground">
            Creating draft and opening in Word…
          </p>
          <p className="max-w-[280px] text-center text-xs text-muted-foreground">
            Keep this task pane open until the document finishes loading.
          </p>
        </div>
      ) : null}
      <div>
        <h2 className="text-xl font-semibold text-foreground">Template Library</h2>
        <p className="text-sm text-muted-foreground">Choose a template to review</p>
      </div>

      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search templates..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            disabled={isOpeningDraft}
            className="w-full rounded-lg border border-[var(--border)] bg-input-background py-2 pl-9 pr-3 text-sm disabled:cursor-not-allowed disabled:opacity-60"
          />
        </div>
        <div className="relative">
          <button
            type="button"
            aria-label="Filter templates by date range"
            title={activeDateFilterLabel ?? "Filter templates by date range"}
            className={`inline-flex h-10 w-10 items-center justify-center rounded-lg border bg-input-background text-sm transition-all duration-200 ease-in-out ${
              activeDateFilterLabel ? "border-primary text-primary" : "border-[var(--border)] text-foreground"
            } disabled:cursor-not-allowed disabled:opacity-60`}
            disabled={isOpeningDraft}
            onClick={() => setIsDateFilterOpen((prev) => !prev)}
          >
            <Funnel className="h-4 w-4" aria-hidden />
          </button>
          {isDateFilterOpen && (
            <div className="absolute right-0 z-10 mt-1 w-44 rounded-md border border-[var(--border)] bg-white py-1 shadow-lg">
              <button
                type="button"
                className="block w-full px-3 py-2 text-left text-sm hover:bg-accent"
                onClick={() => {
                  setSelectedDateRangeDays(undefined);
                  setIsDateFilterOpen(false);
                }}
              >
                All dates
              </button>
              {CONTRACT_DATE_RANGE_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className="block w-full px-3 py-2 text-left text-sm hover:bg-accent"
                  onClick={() => {
                    setSelectedDateRangeDays(option.value);
                    setIsDateFilterOpen(false);
                  }}
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {filteredTemplates.length === 0 ? (
        <p className="py-8 text-center text-sm text-muted-foreground">
          {templates.length === 0
            ? "No templates available"
            : "No templates match your search or filter"}
        </p>
      ) : (
        <>
          <DataTable
            columns={columns}
            data={filteredTemplates}
            customStyles={dataTableCustomStyles}
            pagination
            paginationPerPage={DEFAULT_LIST_PAGE_SIZE}
            paginationRowsPerPageOptions={[DEFAULT_LIST_PAGE_SIZE, 20, 30]}
            paginationComponent={AddinDataTablePagination}
            onRowClicked={(row) => {
              if (!isOpeningDraft) {
                onTemplateSelect(row.id);
              }
            }}
            pointerOnHover={!isOpeningDraft}
            noDataComponent={
              <div className="py-8 text-center text-sm text-muted-foreground">
                No templates match your search or filter
              </div>
            }
          />
        </>
      )}
    </div>
  );
};
